import { readonly, ref, useContext } from '@nuxtjs/composition-api';

import { useUiNotification } from '~/composables/useUiNotification';
import { Logger } from '~/helpers/logger';
import { CreateAirwallexSessionParams, SetPaymentParams, UsePaymentAirwallexInterface } from '~/modules/checkout/composables/usePaymentAirwallex/usePaymentAirwallex';

import createAirwallexSession from './commands/createAirwallexSession.gql';
import getInitAirwallex from './commands/getInitAirwallex.gql';
import setPaymentMethodOnCart from './commands/setPaymentMethodOnCart.gql';

/**
 * Allows fetching shipping methods available for a given cart.
 *
 * See the {@link UseGetServiceTaxInterface} for a list of methods and values available in this composable.
 */
export function usePaymentAirwallex(): UsePaymentAirwallexInterface {
  const loading = ref(false);
  const error = ref({ load: null });
  const { app } = useContext();
  const { context } = app;
  const { send: sendNotification } = useUiNotification();

  const create = async (params: CreateAirwallexSessionParams): Promise<any> => {
    Logger.debug('usePaymentAirwallex/create');
    let result = {};
    try {
      loading.value = true;
      const { data, errors } = await context.$vsf.$magento.api.customQuery({
        query: createAirwallexSession,
        queryVariables: {
          cart_id: params.cart_id,
          return_url: params.return_url,
          customer_email: params.customer_email
        },
        customHeaders: params?.customHeaders,
      });
      Logger.debug('[Result]:', { data });
      if (errors && errors?.length) {
        sendNotification({
          id: Symbol('make_order_error'),
          message: errors[0]?.message as string,
          type: 'danger',
          icon: 'info',
          persist: true,
        });
      }
      console.log('Airwallex session data:', data);
      result = data?.createAirwallexSession || null;
      error.value.load = null;
    } catch (err) {
      error.value.load = err;
      Logger.error('usePaymentAirwallex/create', err);
    } finally {
      loading.value = false;
    }
    return result;
  };

  const setPayment = async (params: SetPaymentParams): Promise<any> => {
    Logger.debug('usePaymentAirwallex/setPayment');
    let result = {};
    try {
      loading.value = true;
      const { data } = await context.$vsf.$magento.api.customQuery({
        query: setPaymentMethodOnCart,
        queryVariables: {
          cart_id: params.cart_id,
          code: params.code,
          payment_intent_id: params.payment_intent_id,
          client_secret: params.client_secret,
          payment_method: params.payment_method,
        },
        customHeaders: params?.customHeaders,
      });
      Logger.debug('[Result]:', { data });
      result = data?.setPaymentMethodOnCart || null;
      error.value.load = null;
    } catch (err) {
      error.value.load = err;
      Logger.error('usePaymentAirwallex/setPayment', err);
    } finally {
      loading.value = false;
    }
    return result;
  };

  const getInitConfig = async (): Promise<any> => {
    Logger.debug('usePaymentAirwallex/getInitConfig');
    let result = {};
    try {
      loading.value = true;
      const { data } = await context.$vsf.$magento.api.customQuery({
        query: getInitAirwallex,
      });
      Logger.debug('[Result]:', { data });
      result = data?.airwallexConfig || null;
      error.value.load = null;
    } catch (err) {
      error.value.load = err;
      Logger.error('usePaymentAirwallex/getInitConfig', err);
    } finally {
      loading.value = false;
    }
    return result;
  };

  return {
    create,
    setPayment,
    getInitConfig,
    loading: readonly(loading),
    error: readonly(error),
  };
}
export default usePaymentAirwallex;
export * from './usePaymentAirwallex';
